/**
 * Part C SP4 S5 — passage-offset deep-link RESOLVER (reader side).
 *
 * Given a citation (or a SED `LinkedDocumentRef`), resolve the most
 * specific deep-link target available, preferring:
 *
 *   1. `offset`   — a character span into a specific document
 *                   (`PassageOffset` / `Citation.passageOffset`), so the
 *                   viewer can scroll to / highlight the exact passage.
 *   2. `page`     — a 1-based page number (today's REF-LINEAGE behaviour).
 *   3. `filename` — fall back to scroll-to-appendix / open-the-doc with
 *                   no in-document position.
 *
 * READER ONLY — this slice ships the consumer-side projection. No
 * producer populates `passageOffset` / `sourceOffset` yet (population is
 * a follow-on SED step). The function therefore degrades cleanly to the
 * page → filename path for every citation currently in storage.
 *
 * Pure projection — never mutates input, never throws, no I/O.
 */
import type { Citation } from '../../types/portfolioComposite';
import type { LinkedDocumentRef } from '../../types/siteEvidence';
import type { SiteDocument as SiteDocumentLike } from '../../types/siteRag';
import type { PassageOffset } from '../../types/siteContext';

/** How specific the resolved deep-link is — drives the viewer behaviour. */
export type DeepLinkPrecision = 'offset' | 'page' | 'filename' | 'none';

export interface CitationDeepLink {
  /** Document the link points at, when known. */
  docId?: string;
  /** Filename for the appendix/open-doc fallback, when resolvable. */
  fileName?: string;
  /** 1-based page, when only page-level lineage exists. */
  page?: number;
  /** Character span `[start, end)` into the doc's text, when present. */
  offset?: { start: number; end: number };
  /** Which granularity the resolver landed on. */
  precision: DeepLinkPrecision;
}

function isValidOffset(o: { start?: unknown; end?: unknown } | undefined | null): o is PassageOffset {
  if (!o) return false;
  const { start, end } = o as { start?: unknown; end?: unknown };
  return (
    typeof start === 'number' && typeof end === 'number'
    && Number.isFinite(start) && Number.isFinite(end)
    && start >= 0 && end > start
  );
}

/**
 * Resolve a `Citation` to its deep-link target. Prefers `passageOffset`
 * (carries its own `docId`), then `page`, then bare `docId`/filename.
 *
 * @param citation The citation to resolve.
 * @param docNameById Optional `docId → fileName` lookup so the result
 *   can carry a filename for the appendix fallback. Omit when the caller
 *   only needs the position.
 */
export function resolveCitationDeepLink(
  citation: Citation | undefined | null,
  docNameById?: ReadonlyMap<string, string> | Record<string, string>,
): CitationDeepLink {
  if (!citation) return { precision: 'none' };

  const lookupName = (id: string | undefined): string | undefined => {
    if (!id || !docNameById) return undefined;
    if (docNameById instanceof Map) return docNameById.get(id);
    return (docNameById as Record<string, string>)[id];
  };

  // 1. Offset — most specific. The offset carries its own docId.
  if (isValidOffset(citation.passageOffset)) {
    const off = citation.passageOffset!;
    return {
      docId: off.docId,
      fileName: lookupName(off.docId),
      offset: { start: off.start, end: off.end },
      precision: 'offset',
    };
  }

  // 2. Page-level lineage.
  if (typeof citation.page === 'number' && citation.page > 0) {
    return {
      docId: citation.docId,
      fileName: lookupName(citation.docId),
      page: citation.page,
      precision: 'page',
    };
  }

  // 3. Filename / open-doc fallback.
  if (citation.docId) {
    return { docId: citation.docId, fileName: lookupName(citation.docId), precision: 'filename' };
  }

  return { precision: 'none' };
}

/**
 * Resolve a SED `LinkedDocumentRef` to its deep-link target. The ref is
 * already scoped to one doc (`ref.docId`), so the offset has no `docId`
 * of its own. Same offset → page → filename preference order.
 */
export function resolveLinkedDocDeepLink(
  ref: LinkedDocumentRef | undefined | null,
  doc?: Pick<SiteDocumentLike, 'fileName'> | { fileName?: string },
): CitationDeepLink {
  if (!ref) return { precision: 'none' };
  const fileName = doc?.fileName;

  if (isValidOffset(ref.sourceOffset)) {
    return {
      docId: ref.docId,
      fileName,
      offset: { start: ref.sourceOffset!.start, end: ref.sourceOffset!.end },
      precision: 'offset',
    };
  }

  if (typeof ref.pageNumber === 'number' && ref.pageNumber > 0) {
    return { docId: ref.docId, fileName, page: ref.pageNumber, precision: 'page' };
  }

  return { docId: ref.docId, fileName, precision: 'filename' };
}
